import React from 'react';
import { Link } from 'react-router-dom';

const isExternal = (url) => /^(https?:)?\/\//i.test(url) || /^(mailto|tel):/i.test(url);

const renderLink = (text, url, key) => {
  if (isExternal(url)) {
    return <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="text-[#006D77] underline underline-offset-2 hover:text-[#38B2AC]">{text}</a>;
  }
  return <Link key={key} to={url} className="text-[#006D77] underline underline-offset-2 hover:text-[#38B2AC]">{text}</Link>;
};

export const inline = (text, keyBase) => {
  const parts = text.split(/(\[[^\]]+\]\([^)\s]+\)|\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g).filter(Boolean);
  return parts.map((p, i) => {
    const link = p.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) return renderLink(inline(link[1], `${keyBase}-${i}-l`), link[2], `${keyBase}-${i}`);
    if (p.startsWith('**') && p.endsWith('**')) return <strong key={`${keyBase}-${i}`} className="font-semibold text-[#1D1D1F]">{inline(p.slice(2, -2), `${keyBase}-${i}-b`)}</strong>;
    if (p.startsWith('*') && p.endsWith('*') && p.length > 2) return <em key={`${keyBase}-${i}`}>{inline(p.slice(1, -1), `${keyBase}-${i}-i`)}</em>;
    if (p.startsWith('`') && p.endsWith('`')) return <code key={`${keyBase}-${i}`} className="bg-black/5 rounded px-1 text-[0.9em]">{p.slice(1, -1)}</code>;
    return p;
  });
};

// Minimal Markdown renderer: headings, bold/italic, bullet lists, paragraphs. No HTML.
export const MarkdownText = ({ text, className = '' }) => {
  if (!text) return null;
  const lines = text.replace(/\r/g, '').split('\n');
  const blocks = [];
  let para = [];
  let list = null;

  const flushPara = () => {
    if (para.length) { blocks.push({ type: 'p', text: para.join(' ') }); para = []; }
  };
  const flushList = () => {
    if (list) { blocks.push(list); list = null; }
  };

  for (const raw of lines) {
    const line = raw.trim();
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    const li = line.match(/^(?:[-*•]|\d+[.)])\s+(.*)$/);
    if (!line) { flushPara(); flushList(); continue; }
    if (h) { flushPara(); flushList(); blocks.push({ type: `h${h[1].length}`, text: h[2] }); continue; }
    if (li) { flushPara(); if (!list) list = { type: 'ul', items: [] }; list.items.push(li[1]); continue; }
    flushList();
    para.push(line);
  }
  flushPara(); flushList();

  return (
    <div className={`space-y-2 ${className}`}>
      {blocks.map((b, i) => {
        if (b.type === 'ul') {
          return (
            <ul key={i} className="space-y-1 pl-1">
              {b.items.map((it, j) => (
                <li key={j} className="flex gap-2"><span className="mt-[0.45em] h-1.5 w-1.5 rounded-full bg-current opacity-60 flex-shrink-0" /><span>{inline(it, `${i}-${j}`)}</span></li>
              ))}
            </ul>
          );
        }
        if (b.type.startsWith('h')) {
          const cls = b.type === 'h1' ? 'text-lg font-bold mt-3' : b.type === 'h2' ? 'text-base font-bold mt-3' : 'text-sm font-semibold uppercase tracking-wide mt-2';
          return <p key={i} className={`${cls} text-[#1D1D1F]`}>{inline(b.text, i)}</p>;
        }
        return <p key={i}>{inline(b.text, i)}</p>;
      })}
    </div>
  );
};

export default MarkdownText;
